'use client';

import { useEffect, useState } from 'react';
import { useControlledComponent } from 'hooks/useControlledComponent';
import { usePrevious } from 'hooks/usePrevious';

export type UseLazyLoadOptions = {
  itemCount: number;
  lazyLoad?: boolean;
  currentPage: number;
  loaded?: Set<number>;
  onRequestChange?: (next: Set<number>) => void;
};

export function useLazyLoad({
  lazyLoad = true,
  itemCount,
  currentPage,
  loaded: loadedProp,
  onRequestChange,
}: UseLazyLoadOptions) {
  const [defaultSet] = useState(() => new Set([currentPage]));

  const [loaded, setLoaded] = useControlledComponent(
    loadedProp,
    onRequestChange,
    defaultSet
  );

  const maxIndex = itemCount - 1;
  const prevIndex = usePrevious(currentPage);
  /**
   * Lazy load panel content
   */

  useEffect(() => {
    function isValidNextIndex(index: number) {
      return index > 0 && index <= maxIndex;
    }

    function addIndexToLoaded(index: number) {
      if (!isValidNextIndex(index)) {
        return;
      }

      const indexes: number | number[] = index;
      const indexesArray = Array.isArray(indexes) ? indexes : [indexes];
      const next = new Set(loaded);

      indexesArray.forEach((i) => {
        // don't set items which are already loaded or are invalid
        // @ts-expect-error FIXME(strictNullChecks)
        if (loaded.has(i) || !isValidNextIndex(index)) {
          return;
        }

        next.add(i);
      });

      setLoaded(next);
    }

    if (lazyLoad && prevIndex !== currentPage) {
      addIndexToLoaded(currentPage);
    }
  }, [prevIndex, currentPage, loaded, maxIndex, lazyLoad, setLoaded]);

  return {
    loaded,
    shouldLoadIndex: (i: number) => {
      // @ts-expect-error FIXME(strictNullChecks)
      return !lazyLoad || currentPage === i || loaded.has(i);
    },
  };
}
